/**
 * Intake desk suggestions (PRD §9.3). Pure ranking over candidates the caller already loaded.
 * Suggests; never decides — the office always presses Confirm.
 */
import type { FluidType } from './lifecycle';
import { normalizeSampleId } from './identifiers';

export interface ExpectedCandidate {
  readonly fluidSampleId: string;
  readonly fluidType: FluidType;
  readonly serviceTicketId: string | null;
  readonly customerName: string | null;
  readonly unitName: string | null;
  readonly technician: string | null;
  readonly collectedAt: number | null;
  readonly sampleIdFromFsr: string | null;
  readonly reportedFluids: readonly FluidType[];
}

export interface TicketCandidate {
  readonly serviceTicketId: string;
  readonly customerName: string | null;
  readonly unitName: string | null;
  readonly technician: string | null;
  readonly visitAt: number | null;
  readonly generatorIds: readonly string[];
}

export type IntakeGroup = 'fsr_sample_id' | 'fsr_reported' | 'ticket_no_fsr';

export interface IntakeSuggestion {
  readonly group: IntakeGroup;
  readonly key: string;
  readonly label: string;
  readonly expected?: ExpectedCandidate;
  readonly ticket?: TicketCandidate;
}

const DAY = 86_400_000;
const EXPECTED_WINDOW_DAYS = 45;
const TICKET_WINDOW_DAYS = 14;

const haystack = (...parts: ReadonlyArray<string | null>): string => parts.filter(Boolean).join(' ').toLowerCase();

function matchesSearch(search: string, text: string): boolean {
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  return terms.every((t) => text.includes(t));
}

const within = (at: number | null, days: number, now: number): boolean => at !== null && now - at <= days * DAY;

const describe = (customer: string | null, unit: string | null, ticket: string | null): string =>
  [customer, unit, ticket].filter(Boolean).join(' · ');

export function rankIntakeCandidates(input: {
  readonly scannedSampleId: string;
  readonly fluidType: FluidType | null;
  readonly search: string;
  readonly now: number;
  readonly expected: readonly ExpectedCandidate[];
  readonly tickets: readonly TicketCandidate[];
}): { suggestions: IntakeSuggestion[]; highlightKey: string | null } {
  const scanned = normalizeSampleId(input.scannedSampleId);
  const text = input.search.trim();

  const ofFluid = input.expected.filter((e) => input.fluidType === null || e.fluidType === input.fluidType);
  const searchable = (e: ExpectedCandidate) => haystack(e.customerName, e.unitName, e.serviceTicketId, e.technician);

  // An exact sample-ID match is evidence, not a guess, so it ignores the recency window: a bottle can
  // reach the office long after its FSR. The windows below only bound the recency GUESSES; an
  // undated candidate is never offered as one (there is nothing to rank it by), so the only way it
  // appears here is an exact sample-ID match.
  const exact = ofFluid.filter((e) => scanned !== null && normalizeSampleId(e.sampleIdFromFsr) === scanned);
  const exactIds = new Set(exact.map((e) => e.fluidSampleId));

  const reported = ofFluid
    .filter((e) => !exactIds.has(e.fluidSampleId))
    .filter((e) => within(e.collectedAt, EXPECTED_WINDOW_DAYS, input.now))
    .filter((e) => text === '' || matchesSearch(text, searchable(e)))
    .sort((a, b) => (b.collectedAt ?? 0) - (a.collectedAt ?? 0));

  const coveredTickets = new Set(reported.concat(exact).map((e) => e.serviceTicketId).filter((v): v is string => v !== null));
  const tickets = input.tickets
    .filter((t) => within(t.visitAt, TICKET_WINDOW_DAYS, input.now))
    .filter((t) => !coveredTickets.has(t.serviceTicketId))
    .filter((t) => text === '' || matchesSearch(text, haystack(t.customerName, t.unitName, t.serviceTicketId, t.technician)))
    .sort((a, b) => (b.visitAt ?? 0) - (a.visitAt ?? 0));

  const suggestions: IntakeSuggestion[] = [
    ...exact.map((e): IntakeSuggestion => ({ group: 'fsr_sample_id', key: `expected:${e.fluidSampleId}`, label: describe(e.customerName, e.unitName, e.serviceTicketId), expected: e })),
    ...reported.map((e): IntakeSuggestion => ({ group: 'fsr_reported', key: `expected:${e.fluidSampleId}`, label: describe(e.customerName, e.unitName, e.serviceTicketId), expected: e })),
    ...tickets.map((t): IntakeSuggestion => ({ group: 'ticket_no_fsr', key: `ticket:${t.serviceTicketId}`, label: describe(t.customerName, t.unitName, t.serviceTicketId), ticket: t })),
  ];

  const highlightKey = exact.length === 1 ? `expected:${exact[0].fluidSampleId}` : suggestions.length === 1 ? suggestions[0].key : null;
  return { suggestions, highlightKey };
}
